import React from 'react';
import { FieldArray, FormikProps } from 'formik';
import { IonButton, IonCol, IonLabel, IonRow } from '@ionic/react';
import { useIntl } from 'react-intl';
import { FirmwareMessages } from 'src/intl/FirmwareMessages';
import { Organization } from 'src/models/organization';
import { FirmwareDetailsData } from './types';
import { FirmwareTier } from '../../models/FirmwareTier';
import { Input } from '../reusable-components/Input';
import { InputSelect } from '../reusable-components/InputSelect';

interface AddFirmwareDetailsFormProps {
  /**
   * State, handlers, and helpers made available to form component or render prop of.
   */
  formikProps: FormikProps<FirmwareDetailsData>;
  /**
   * The Organizations options for a beta user.
   */
  organizations: Organization[];

  /**
   * The firmware tier options.
   */
  firmwareTiers: FirmwareTier[];
  /**
   * Whether the firmware is being updated
   */
  update: boolean;
}

const AddFirmwareDetailsForm: React.FC<AddFirmwareDetailsFormProps> = ({
  formikProps,
  organizations,
  update,
  firmwareTiers,
}) => {
  const { formatMessage } = useIntl();
  const releaseType = formikProps.values.deploymentType;

  const deploymentTypeSelectOptions = [
    { value: 'test', label: formatMessage(FirmwareMessages.fieldTest) },
    { value: 'global', label: formatMessage(FirmwareMessages.global) },
    { value: 'dev', label: formatMessage(FirmwareMessages.dev) },
    { value: 'custom', label: formatMessage(FirmwareMessages.custom) },
    { value: 'beta', label: formatMessage(FirmwareMessages.beta) },
    { value: 'paid', label: formatMessage(FirmwareMessages.paid) },
  ];

  const archiveTypeSelectOptions = [
    { label: formatMessage(FirmwareMessages.archived), value: 'true' },
    { label: formatMessage(FirmwareMessages.notArchived), value: 'false' },
  ];

  const organizationSelectOptions = organizations.map(org => {
    return {
      value: org._id,
      label: org.orgName,
    };
  });

  return (
    <>
      {!update && (
        <Input
          label={formatMessage(FirmwareMessages.binFile)}
          id="firmware-bin-file"
          value={formikProps.values.binFile}
          placeholder={formatMessage(FirmwareMessages.binFile)}
          onChange={formikProps.handleChange}
          name="binFile"
        />
      )}
      <Input
        label={formatMessage(FirmwareMessages.displayName)}
        value={formikProps.values.displayName}
        placeholder={formatMessage(FirmwareMessages.displayNamePlaceholder)}
        onChange={formikProps.handleChange}
        name="displayName"
        id="add-firmware-display-name"
        errorText={formikProps.touched.displayName ? formikProps.errors.displayName : undefined}
        required
      />

      <Input
        label={formatMessage(FirmwareMessages.fwVersion)}
        value={formikProps.values.fwVersion}
        placeholder={formatMessage(FirmwareMessages.fwVersionPlaceholder)}
        onChange={formikProps.handleChange}
        name="fwVersion"
        id="add-firmware-fw-version"
        errorText={formikProps.touched.fwVersion ? formikProps.errors.fwVersion : undefined}
        required
      />

      <InputSelect<string>
        label={formatMessage(FirmwareMessages.archiveFirmware)}
        options={archiveTypeSelectOptions}
        value={formikProps.values.archiveFirmware}
        onChange={e => formikProps.setFieldValue('archiveFirmware', e)}
        placeholder={formatMessage(FirmwareMessages.archiveFirmwarePlaceholder)}
        onBlur={formikProps.handleBlur}
        name="archiveFirmware"
        id="add-firmware-archive-firmware"
        errorText={formikProps.touched.archiveFirmware ? formikProps.errors.archiveFirmware : undefined}
        required
      />

      <InputSelect<string>
        label={formatMessage(FirmwareMessages.deploymentType)}
        options={deploymentTypeSelectOptions}
        value={formikProps.values.deploymentType}
        onChange={e => {
          formikProps.setFieldValue('deploymentType', e);
          formikProps.setFieldValue('users', []);
        }}
        placeholder={formatMessage(FirmwareMessages.deploymentType)}
        onBlur={formikProps.handleBlur}
        name="deploymentType"
        id="add-firmware-deployment-type"
        errorText={formikProps.touched.deploymentType ? formikProps.errors.deploymentType : undefined}
        required
      />
      {releaseType === 'custom' && (
        <InputSelect<string>
          label={formatMessage(FirmwareMessages.organizationForCustomSoftware)}
          value={formikProps.values.organization}
          onChange={e => formikProps.setFieldValue('organization', e)}
          onBlur={formikProps.handleBlur}
          options={organizationSelectOptions}
          placeholder={formatMessage(FirmwareMessages.firmwareOrgPlaceholder)}
          name="organization"
          id="add-firmware-organization"
          errorText={formikProps.touched.organization ? formikProps.errors.organization : undefined}
          required={releaseType === 'custom'}
        />
      )}
      {(releaseType === 'beta' || releaseType === 'test') && (
        <FieldArray name="users">
          {({ push, remove }) => (
            <>
              <IonRow className="ion-margin-top ion-align-items-center ion-justify-content-start">
                <IonLabel>
                  {releaseType === 'beta'
                    ? formatMessage(FirmwareMessages.betaUsers)
                    : formatMessage(FirmwareMessages.userEmails)}
                </IonLabel>
                <IonButton className="ion-padding-start" expand="block" onClick={() => push('')}>
                  {formatMessage(FirmwareMessages.addUserEmail)}
                </IonButton>
              </IonRow>
              <div>
                {formikProps?.values?.users?.map((item, index) => {
                  const users = `users[${index}]`;
                  return (
                    <>
                      <IonRow key={`${index}`} className="ion-margin-top">
                        <IonLabel>
                          {formatMessage(FirmwareMessages.emailAddressCount, {
                            number: index + 1,
                          })}
                        </IonLabel>
                      </IonRow>
                      <IonRow className="ion-align-items-center">
                        <IonCol size="11">
                          <Input
                            value={item}
                            placeholder={formatMessage(FirmwareMessages.addUserEmail)}
                            onChange={formikProps.handleChange}
                            name={users}
                            id="add-firmware-user-email"
                          />
                        </IonCol>
                        <IonCol size="1">
                          <IonButton onClick={() => remove(index)}>x</IonButton>
                        </IonCol>
                      </IonRow>
                    </>
                  );
                })}
              </div>
            </>
          )}
        </FieldArray>
      )}
      {releaseType === 'test' && (
        <FieldArray name="organizations">
          {({ push, remove }) => (
            <>
              <IonRow className="ion-margin-top ion-align-items-center ion-justify-content-start">
                <IonLabel> {formatMessage(FirmwareMessages.organizations)}</IonLabel>
                <IonButton className="ion-padding-start" expand="block" onClick={() => push('')}>
                  {formatMessage(FirmwareMessages.addOrganizations)}
                </IonButton>
              </IonRow>
              <div>
                {formikProps?.values?.organizations.map((item, index) => {
                  const testOrganizations = `organizations[${index}]`;
                  return (
                    <>
                      <IonRow className="ion-margin-top">
                        <IonLabel>
                          {formatMessage(FirmwareMessages.organizationCount, {
                            number: index + 1,
                          })}
                        </IonLabel>
                      </IonRow>
                      <IonRow className="ion-align-items-center">
                        <IonCol size="11">
                          <InputSelect<string>
                            value={item}
                            onChange={formikProps.handleChange}
                            onBlur={formikProps.handleBlur}
                            options={organizationSelectOptions}
                            placeholder={formatMessage(FirmwareMessages.firmwareOrgPlaceholder)}
                            name={testOrganizations}
                            id="add-firmware-organization"
                          />
                        </IonCol>
                        <IonCol size="1">
                          <IonButton onClick={() => remove(index)}>x</IonButton>
                        </IonCol>
                      </IonRow>
                    </>
                  );
                })}
              </div>
            </>
          )}
        </FieldArray>
      )}
      {releaseType === 'paid' && (
        <>
          <IonLabel className="ion-padding-top" mode="md">
            {formatMessage(FirmwareMessages.firmwareTier)}
          </IonLabel>
          <InputSelect<string>
            label={formatMessage(FirmwareMessages.firmwareTier)}
            options={firmwareTiers.map(ft => ({ label: ft.tierName, value: ft._id }))}
            value={formikProps.values.firmwareTier}
            onChange={formikProps.handleChange}
            onBlur={formikProps.handleBlur}
            placeholder={formatMessage(FirmwareMessages.firmwareTierPlaceholder)}
            name="firmwareTier"
            id="add-firmware-firmware-tier"
            errorText={formikProps.touched.firmwareTier ? formikProps.errors.firmwareTier : undefined}
            required={releaseType === 'paid'}
          />
        </>
      )}
    </>
  );
};

export default AddFirmwareDetailsForm;
